import { useEffect, useMemo } from 'react';
import { useFormManagementQuestionnairesQuery } from 'src/graphql/generated';
import { mapQuestionnairesToFormData } from 'src/components/pages/CoiFormManagement/mapQuestionnaireToFormData';
import {
  FormStatusTab,
  FormType,
} from 'src/components/pages/CoiFormManagement/types';

export type LinkedFormStatus =
  | 'Published'
  | 'Draft'
  | 'Unpublished'
  | 'Archived';

export interface LinkedForm {
  id: string;
  title: string;
  status: LinkedFormStatus;
  createdBy: string;
}

const FORM_STATUS_LABELS: Record<FormStatusTab, LinkedFormStatus> = {
  [FormStatusTab.ACTIVE]: 'Published',
  [FormStatusTab.DRAFTS]: 'Draft',
  [FormStatusTab.UNPUBLISHED]: 'Unpublished',
  [FormStatusTab.ARCHIVED]: 'Archived',
};

/**
 * Resolves forms linked to a category. Works for both Speak Up and COI:
 * pass `formType = FormType.CONFLICT_OF_INTEREST` for COI categories.
 *
 * Filters by the category's stable ID (an exact-match filter in the QA
 * service), not by name. The QA service only stores a category's name as a
 * legacy free-text label frozen at form-creation time — matching on name
 * meant a category rename left this list unable to find its own forms
 * (VAULT-20220), even though the form was still linked by ID.
 */
export const useCategoryLinkedForms = (
  categoryId: number | undefined,
  enabled: boolean,
  formType: FormType = FormType.SPEAK_UP,
) => {
  const { data, isLoading, isError, error } =
    useFormManagementQuestionnairesQuery(
      { filters: categoryId ? { categoryId } : undefined },
      { enabled: enabled && !!categoryId, refetchOnWindowFocus: false },
    );

  useEffect(() => {
    if (isError) {
      console.error('[CategoryLinkedForms] Failed to load linked forms', {
        categoryId,
        error,
      });
    }
  }, [isError, error, categoryId]);

  const forms = useMemo<LinkedForm[]>(() => {
    const questionnaires = data?.formManagementQuestionnaires ?? [];
    if (!categoryId) return [];

    return mapQuestionnairesToFormData(questionnaires)
      .filter(
        (form) => form.type === formType && form.categoryId === categoryId,
      )
      .map((form) => ({
        id: form.id,
        title: form.title,
        status: FORM_STATUS_LABELS[form.status],
        createdBy: form.createdBy,
      }));
  }, [data?.formManagementQuestionnaires, categoryId, formType]);

  return { forms, isLoading, isError, error };
};
